/**
 * The HTTP client.
 *
 * The console is online-assumed (ARCHITECTURE.md), so there is no outbox and no queue here —
 * a failed request is a failed request and the user is told so, in Arabic. The one piece of
 * machinery that does exist is the refresh: an access token expires while a manager reads a
 * job for twenty minutes, and logging him out mid-read would be a bug, not security.
 */

import type { RefreshResponse } from "./types.js";

const BASE = (import.meta.env.VITE_API_URL as string | undefined) ?? "/api";

const ACCESS_KEY = "fanni.console.access";
const REFRESH_KEY = "fanni.console.refresh";

export const tokens = {
  access: (): string | null => localStorage.getItem(ACCESS_KEY),
  refresh: (): string | null => localStorage.getItem(REFRESH_KEY),
  set(access: string, refresh: string): void {
    localStorage.setItem(ACCESS_KEY, access);
    localStorage.setItem(REFRESH_KEY, refresh);
  },
  clear(): void {
    localStorage.removeItem(ACCESS_KEY);
    localStorage.removeItem(REFRESH_KEY);
  },
};

/**
 * Every API error the console can show a human. `message` is the API's own Arabic string
 * where it sent one (middleware.ts `ERR_AR`) — we do not re-translate it, because the server
 * knows which of six refusals it made and the client does not.
 */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly detail?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

const NETWORK_AR = "تعذّر الاتصال بالخادم. تحقق من الشبكة.";

let refreshing: Promise<boolean> | null = null;

/** Single-flight: five queries expiring together must cause one refresh, not five. */
async function refreshTokens(): Promise<boolean> {
  if (refreshing) return refreshing;
  refreshing = (async () => {
    const rt = tokens.refresh();
    if (!rt) return false;
    try {
      const res = await fetch(`${BASE}/auth/refresh`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({ refresh_token: rt }),
      });
      if (!res.ok) return false;
      const body = (await res.json()) as RefreshResponse;
      tokens.set(body.access_token, body.refresh_token);
      return true;
    } catch {
      return false;
    } finally {
      refreshing = null;
    }
  })();
  return refreshing;
}

/** Fired when the refresh itself fails — the session is genuinely over. */
export const SESSION_EXPIRED = "fanni:session-expired";

interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "DELETE";
  body?: unknown;
  query?: Record<string, string | number | undefined | null>;
  /** Internal: the retry-after-refresh guard. */
  _retried?: boolean;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const url = new URL(`${BASE}${path}`, window.location.origin);
  for (const [k, v] of Object.entries(opts.query ?? {})) {
    if (v !== undefined && v !== null && v !== "") url.searchParams.set(k, String(v));
  }

  const headers: Record<string, string> = {};
  const access = tokens.access();
  if (access) headers.authorization = `Bearer ${access}`;
  if (opts.body !== undefined) headers["content-type"] = "application/json";

  let res: Response;
  try {
    res = await fetch(url.toString().replace(window.location.origin, ""), {
      method: opts.method ?? "GET",
      headers,
      body: opts.body === undefined ? undefined : JSON.stringify(opts.body),
    });
  } catch {
    throw new ApiError(0, "network", NETWORK_AR);
  }

  if (res.status === 401 && !opts._retried && tokens.refresh()) {
    if (await refreshTokens()) return api<T>(path, { ...opts, _retried: true });
    tokens.clear();
    window.dispatchEvent(new CustomEvent(SESSION_EXPIRED));
  }

  const text = await res.text();
  const body: unknown = text ? safeJson(text) : null;

  if (!res.ok) {
    const e = (body ?? {}) as { error?: string; message?: string; detail?: unknown };
    throw new ApiError(res.status, e.error ?? "unknown", e.message ?? "حدث خطأ غير متوقع.", e.detail);
  }
  return body as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}
